<template lang="html">
  <sc-flex :is-column="true" :is-full-width="true">
    <signing-completed-dialog
      :opened.sync="signingCompletedDialogOpened"
      :taxReturn="taxReturn"
      :hasNextStep="hasNextStep"
      @go-to-next-step="done"
    />

    <input
      type="file"
      ref="uploadInput"
      @click="resetFilePicker()"
      @input="uploadSignedForm($event)"
      hidden
    />

    <!-- Partial: Alert -->
    <sc-flex class="sc-alert" :is-between="true" :is-y-centered="true" :gap="8">
      <sc-flex :is-y-centered="true" :gap="8">
        <sc-icon
          name="warning-circle"
          size="normal"
          color="currentcolor"
        ></sc-icon>
        <sc-small :line-clamp="2">
          <b>Instruction:</b> Please download the forms and sign them on papers.
        </sc-small>
      </sc-flex>
      <sc-button-group>
        <sc-button
          type="ghost"
          @click="done()"
          icon-left="arrow-right"
          :is-disabled="!allWetSigned || !hasNextStep"
        >
          Done
        </sc-button>
      </sc-button-group>
    </sc-flex>

    <v-layout class="full-height-container">
      <v-layout column class="pdf-container">
        <div class="download-and-sign-container mt-4">
          <v-layout>
            <h3 class="text-xs-left">
              <span
                >The following forms cannot be signed electronically. Kindly
                download and print these forms. After signing the printed
                versions, please upload the signed copies back into the
                system.</span
              >
            </h3>
          </v-layout>

          <v-data-table
            class="mt-3"
            :items="wetSignForms"
            :headers="headers"
            :pagination.sync="pagination"
            :hide-actions="true"
          >
            <template slot="items" slot-scope="props">
              <td class="text-xs-left">
                <span
                  v-if="
                    props.item.form_id === 'other' &&
                      !isEmpty(props.item.custom_form_name)
                  "
                >
                  {{ props.item.custom_form_name }}
                </span>

                <span v-else>
                  {{ props.item.form_name }} ({{ props.item.form_id }})
                </span>
              </td>

              <td class="text-xs-left">
                <v-btn
                  small
                  flat
                  color="primary"
                  class="mt-2 ml-0 text-none"
                  @click="downloadForm(props.item)"
                >
                  <v-icon small class="mr-1"> download </v-icon>
                  Download
                </v-btn>
              </td>

              <td class="text-xs-left">
                <v-btn
                  small
                  flat
                  color="primary"
                  class="mt-2 ml-0 text-none"
                  type="file"
                  :loading="uploading"
                  @click="showUploadDialog(props.item)"
                >
                  <v-icon small class="mr-1"> upload </v-icon>
                  Upload
                </v-btn>
              </td>

              <td class="text-xs-left py-1">
                <div class="mb-1">
                  <SignedDocumentList
                    :taxReturn="taxReturn"
                    :currentCompany="currentCompany"
                    :currentForm="props.item"
                    :hideDownloadButton="true"
                  />
                </div>
              </td>
            </template>
          </v-data-table>
        </div>
      </v-layout>
    </v-layout>
  </sc-flex>
</template>

<script>
import { mapGetters } from 'vuex';
import { PDFDocument } from 'pdf-lib';
import axios from 'axios';
import download from 'downloadjs';
import has from 'lodash/has';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import SignedDocumentList from '@/components/returns/SignedDocumentList';
import API from '@/api';
import SigningCompletedDialog from './components/SigningCompletedDialog';
import SignStepMixin from '@/mixins/SignStepMixin';

export default {
  components: {
    SignedDocumentList,
    SigningCompletedDialog,
  },

  mixins: [SignStepMixin],

  props: {
    companyId: {
      type: String,
      required: true,
    },

    currentCompany: {
      type: Object,
      default: () => {},
    },

    currentIdentity: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      required: true,
    },

    hasNextStep: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      currentFormIndex: 1,

      pdfLoading: false,
      pdfProgress: 0,
      pdfLoaded: false,
      pdfPageIndex: 1,

      uploading: false,

      headers: [
        {
          text: 'Form',
          align: 'left',
          value: 'name',
          width: '25%',
        },
        {
          text: 'Download & sign',
          align: 'left',
        },
        {
          text: 'Upload signed forms',
          align: 'left',
        },
        {
          text: 'Uploaded forms',
          align: 'left',
          width: '40%',
        },
      ],

      pagination: {
        rowsPerPage: -1,
      },

      signingCompletedDialogOpened: false,
    };
  },

  methods: {
    isEmpty,

    done() {
      this.$emit('finished-signing');
    },

    async downloadForm(form) {
      const response = await API.integration.downloadDocument({
        companyId: this.companyId,
        link: this.taxReturn.watermarked_file,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(pdfBytes);

      const formPdf = await PDFDocument.create();
      const [page] = await formPdf.copyPages(pdfDoc, [form.page - 1]);
      formPdf.addPage(page);

      const formBytes = await formPdf.save();
      download(
        formBytes,
        form.form_name + ' ' + form.form_id,
        'application/pdf',
      );
    },

    resetFilePicker() {
      this.$refs.uploadInput.value = '';
    },

    showUploadDialog(form) {
      this.currentForm = form;
      this.$refs.uploadInput.click();
    },

    async uploadSignedForm(e) {
      const currentForm = this.currentForm;
      this.uploading = true;

      try {
        const file = e.target.files[0];

        const {
          data: { id, upload_link },
        } = await API.integration.getTemporaryUploadLink({
          companyId: this.companyId,
        });

        await API.integration.uploadToDropbox(upload_link, file);

        const path = 'Client Manually Signed Forms';

        const response = await API.integration.processDocument({
          id,
          documentName: file.name,
          companyId: this.companyId,
          path: '/'.concat(path),
        });

        // Remove firm name & company name from path
        const list = response.data.document_location.split('/');
        list.splice(0, 3);

        const documentPath = list.join('/');

        await this.saveSignedForm(currentForm, documentPath);
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: has(error, 'message')
            ? get(error, 'mesage')
            : 'There was a problem uploading the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.uploading = false;
      }
    },

    async saveSignedForm(form, documentPath) {
      if (!form.wetSignatures) {
        form.wetSignatures = [];
      }

      form.wetSignatures.push({
        signedDocumentPath: documentPath,
      });

      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.companyId,
        e_sign_forms: this.taxReturn.e_sign_forms,
      });

      this.$emit('tax-return-updated', response.data);

      this.$store.dispatch('application/setSuccessNotification', {
        message: `You have successfully uploaded the document!`,
      });
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    wetSignForms() {
      return this.taxReturn.e_sign_forms.filter(
        form => form.wetSignaturesRequired,
      );
    },

    allWetSigned() {
      return this.wetSignForms.every(form => {
        return form.wetSignatures && form.wetSignatures.length > 0;
      });
    },

    hasWetSignatures() {
      return (
        this.currentForm.wetSignatures &&
        this.currentForm.wetSignatures.length > 0
      );
    },
  },

  watch: {
    allSigned() {
      if (this.allSigned) {
        this.signingCompletedDialogOpened = true;
      }
    },
  },
};
</script>

<style lang="css" scoped>
.sc-alert {
  position: sticky;
  top: 0;

  margin: 0px;
  padding: 8px;

  background-color: var(--blue-600);
  color: var(--gray-0);
}

.full-height-container {
  overflow: hidden;
  background-color: #f0f0f0;
  min-height: calc(100vh - 72px - 52px);
  max-height: calc(100vh - 72px - 52px);
  max-width: 100%;
}

.pdf-container {
  flex: auto;
  min-width: 0;
  align-self: stretch;
  overflow: auto;
}

.pdf-page-container {
  position: relative;
  margin: 0 auto;
  max-width: 100%;
  width: auto;
}

.pdf {
  position: relative;
  padding: 0;
  width: 100%;
}

.button-containers {
  flex-grow: 0;
}

.download-and-sign-container {
  margin: 0 auto;
  background: #fff;
  padding: 30px 30px 20px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
</style>
